import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { FeatureFlagService } from '../services/feature-flag.service';
import { UserService } from '../user/user.service';
import { firstValueFrom, filter, take } from 'rxjs';
export const FeatureFlagGuard: CanActivateFn = async (route, state) => {
   const featureFlagService = inject(FeatureFlagService);
   const userService = inject(UserService);
   const router = inject(Router);

   const featureKey = route.data['featureKey'] as string;
   if (!featureKey) {
      // If no featureKey is specified in the route data, allow access
      return true;
   }

   // Wait for the user to be loaded. `currentUser$` is a BehaviorSubject that
   // emits null until auth resolves, so we take its first non-null value — the
   // same pattern the auth/store guards use. If the user is genuinely
   // unauthenticated this never emits, but authGuard returns false first and
   // Angular cancels navigation (unsubscribing this guard), so it can't hang.
   const user = await firstValueFrom(
      userService.currentUser$.pipe(
         filter((u) => u !== null),
         take(1),
      ),
   );

   if (!user?.storeId) {
      return true; // Let auth guard/store guard handle this
   }

   const storeId = user.storeId;

   // Check if feature is enabled for this store. These async checks resolve
   // from the realtime flag cache (kept fresh by the `flags$` subscription),
   // only hitting Firestore on a cold cache — so routing isn't blocked on a
   // network round-trip on every navigation.
   // `requireExplicitFeature` routes are blocked unless the flag is explicitly
   // enabled (an unmanaged/absent flag counts as disabled).
   const requireExplicit = route.data['requireExplicitFeature'] === true;
   const isEnabled = requireExplicit
      ? await featureFlagService.isExplicitlyEnabled(featureKey, storeId)
      : await featureFlagService.isEnabled(featureKey, storeId);

   if (!isEnabled) {
      // Act as if the route doesn't exist
      return router.createUrlTree(['/404']);
   }

   return true;
};
